import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface PendingApproval {
  id: string;
  organization_id: string;
  action_type: string;
  requester_user_id: string;
  target_owner_id: string | null;
  action_metadata: Record<string, unknown>;
  status: 'pending' | 'approved' | 'denied' | 'expired';
  expires_at: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  // Joined data
  requester_name?: string;
  requester_email?: string;
}

interface PendingApprovalResult {
  id: string;
  organization_id: string;
  action_type: string;
  requester_user_id: string;
  target_owner_id: string | null;
  action_metadata: Record<string, unknown>;
  status: string;
  expires_at: string;
  created_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  requester_name: string | null;
  requester_email: string | null;
}

// Hook for owners to see pending approvals they need to act on
export function usePendingApprovals() {
  const { user, profile } = useAuth();
  const queryClient = useQueryClient();
  const [realtimeChannel, setRealtimeChannel] = useState<RealtimeChannel | null>(null);

  // Fetch pending approvals for this owner
  const {
    data: pendingApprovals,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['pending-approvals', user?.id],
    queryFn: async (): Promise<PendingApproval[]> => {
      if (!user?.id) return [];

      const { data, error } = await supabase.rpc('get_pending_approvals_for_owner', {
        p_owner_user_id: user.id,
      });

      if (error) {
        console.error('Error fetching pending approvals:', error);
        return [];
      }

      return ((data as PendingApprovalResult[]) || []).map((item) => ({
        id: item.id,
        organization_id: item.organization_id,
        action_type: item.action_type,
        requester_user_id: item.requester_user_id,
        target_owner_id: item.target_owner_id,
        action_metadata: item.action_metadata,
        status: item.status as PendingApproval['status'],
        expires_at: item.expires_at,
        created_at: item.created_at,
        resolved_at: item.resolved_at,
        resolved_by: item.resolved_by,
        requester_name: item.requester_name || undefined,
        requester_email: item.requester_email || undefined,
      }));
    },
    enabled: !!user?.id && (profile?.role === 'owner' || profile?.role === 'primary_admin'),
    refetchInterval: 30000, // Refetch every 30 seconds as backup
  });

  // Set up realtime subscription
  useEffect(() => {
    if (!user?.id || !profile?.organization_id) return;

    // Only subscribe if user is owner/primary_admin
    if (profile.role !== 'owner' && profile.role !== 'primary_admin') return;

    const channel = supabase
      .channel(`pending_approvals:${profile.organization_id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_approvals',
          filter: `organization_id=eq.${profile.organization_id}`,
        },
        (payload) => {
          console.log('Pending approval change:', payload);
          // Refetch to get updated data
          refetch();
        }
      )
      .subscribe();

    setRealtimeChannel(channel);

    return () => {
      channel.unsubscribe();
    };
  }, [user?.id, profile?.organization_id, profile?.role, refetch]);

  // Approve a pending request
  const approveMutation = useMutation({
    mutationFn: async ({
      approvalId,
      code,
    }: {
      approvalId: string;
      code: string;
    }): Promise<{ success: boolean }> => {
      // First verify the owner's 2FA code
      const verifyResult = await supabase.rpc('verify_totp_code' as never, {
        p_user_id: user?.id,
        p_code: code,
      } as never);

      if (verifyResult.error) {
        throw new Error('Failed to verify 2FA code');
      }

      const isValid = (verifyResult.data as { valid?: boolean }[])?.[0]?.valid;
      if (!isValid) {
        throw new Error('Invalid 2FA code');
      }

      // Update the approval status
      const { error } = await supabase
        .from('pending_approvals')
        .update({
          status: 'approved',
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id,
        } as never)
        .eq('id', approvalId as never)
        .eq('status', 'pending' as never);

      if (error) throw error;

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });

  // Deny a pending request
  const denyMutation = useMutation({
    mutationFn: async (approvalId: string): Promise<{ success: boolean }> => {
      const { error } = await supabase
        .from('pending_approvals')
        .update({
          status: 'denied',
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id,
        } as never)
        .eq('id', approvalId as never)
        .eq('status', 'pending' as never);

      if (error) throw error;

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    },
  });

  // Format action type for display
  const formatActionType = useCallback((actionType: string): string => {
    const labels: Record<string, string> = {
      void_order: 'Void Order',
      refund: 'Refund',
      cash_adjustment: 'Cash Adjustment',
      discount: 'Discount',
      role_change: 'Role Change',
      user_deactivate: 'User Deactivation',
      primary_admin_change: 'Primary Admin Change',
      disable_2fa: 'Disable 2FA',
      security_settings: 'Security Settings Change',
      export_audit_logs: 'Export Audit Logs',
    };
    return labels[actionType] || actionType.replace(/_/g, ' ');
  }, []);

  // Get action description from metadata
  const getActionDescription = useCallback(
    (approval: PendingApproval): string => {
      const meta = approval.action_metadata;
      if (meta.description && typeof meta.description === 'string') {
        return meta.description;
      }

      // Generate description from metadata
      switch (approval.action_type) {
        case 'void_order':
          return `Void Order #${meta.orderNumber || meta.orderId}`;
        case 'refund':
          return `Refund ${meta.refundType === 'full' ? 'Full' : 'Partial'} - Order #${meta.orderNumber || meta.orderId}`;
        case 'role_change':
          return `Change role to ${meta.newRole}`;
        case 'disable_2fa':
          return 'Disable Two-Factor Authentication';
        default:
          return formatActionType(approval.action_type);
      }
    },
    [formatActionType]
  );

  return {
    pendingApprovals: pendingApprovals || [],
    pendingCount: pendingApprovals?.length || 0,
    isLoading,
    approve: approveMutation.mutateAsync,
    deny: denyMutation.mutateAsync,
    isApproving: approveMutation.isPending,
    isDenying: denyMutation.isPending,
    formatActionType,
    getActionDescription,
    refetch,
    realtimeConnected: !!realtimeChannel,
  };
}

// Hook for requesters to watch for their approval status
export function useWatchApprovalStatus(approvalId: string | null) {
  const [status, setStatus] = useState<'pending' | 'approved' | 'denied' | 'expired' | null>(null);
  const [isWatching, setIsWatching] = useState(false);

  useEffect(() => {
    if (!approvalId) {
      setStatus(null);
      setIsWatching(false);
      return;
    }

    setIsWatching(true);

    // Initial fetch
    supabase
      .from('pending_approvals')
      .select('status')
      .eq('id', approvalId)
      .single()
      .then(({ data }) => {
        if (data) {
          setStatus(data.status as 'pending' | 'approved' | 'denied' | 'expired');
        }
      });

    // Subscribe to changes
    const channel = supabase
      .channel(`approval_status:${approvalId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'pending_approvals',
          filter: `id=eq.${approvalId}`,
        },
        (payload) => {
          const newStatus = payload.new.status as 'pending' | 'approved' | 'denied' | 'expired';
          setStatus(newStatus);
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      setIsWatching(false);
    };
  }, [approvalId]);

  return { status, isWatching };
}
